import type { ReactNode } from 'react';

export type QA = {
  q: string;
  a: ReactNode;
};

/**
 * Built on <details>/<summary>: keyboard operable, screen-reader announced and
 * fully usable before JavaScript loads, with no ARIA needed on top. The panel
 * uses interpolate-size so the open/close height animates where supported and
 * simply snaps where it does not.
 */
export function Accordion({ items, idPrefix }: { items: QA[]; idPrefix: string }) {
  return (
    <div className="flex flex-col gap-3">
      {items.map((item, i) => (
        <details
          key={item.q}
          id={`${idPrefix}-${i}`}
          className="siv-card siv-card-hover group overflow-hidden [&_summary::-webkit-details-marker]:hidden"
        >
          <summary className="flex min-h-16 cursor-pointer list-none items-center justify-between gap-5 px-5 py-4 text-[0.975rem] font-semibold sm:px-7">
            <span className="flex items-center gap-4">
              <span
                aria-hidden="true"
                className="font-mono text-faint text-[0.6875rem] tracking-widest"
              >
                {String(i + 1).padStart(2, '0')}
              </span>
              {item.q}
            </span>
            <span
              aria-hidden="true"
              className="relative grid size-8 shrink-0 place-items-center rounded-full border border-line-strong text-muted transition-[transform,background-color,border-color] duration-400 ease-[var(--ease-siv)] group-open:rotate-135 group-open:border-transparent group-open:bg-brand motion-reduce:transition-none"
            >
              <span className="absolute h-px w-3 bg-current group-open:bg-white" />
              <span className="absolute h-3 w-px bg-current group-open:bg-white" />
            </span>
          </summary>
          <div className="text-muted border-t border-line px-5 py-5 text-[0.95rem] leading-[1.75] sm:px-7 [&_a]:link-siv [&_p+p]:mt-3">
            {item.a}
          </div>
        </details>
      ))}
    </div>
  );
}
